/**
 * Overview fallback fixtures — derived from canonical Demo Brand canon.
 *
 * Used by /overview (the v3 Overview surface) when the real catalog +
 * metrics queries return empty / fail. Numbers come from
 * `lib/fixtures/demo-brand.ts` so this page reconciles with /campaigns,
 * /channels, /budget, /wallet, /alerts, etc.
 *
 * Once channel_performance_daily rollups + a per-channel-top-subs API
 * land, swap these arrays for live data — the consumer shape stays the
 * same.
 */

import {
  DEMO_CHANNELS,
  subchannelsByChannel,
  type DemoChannelKey,
} from '@/lib/fixtures/demo-brand'

export type OverviewChannelKey =
  | 'search'
  | 'social'
  | 'tv'
  | 'programmatic'
  | 'audio'
  | 'retail'

export interface OverviewSubRow {
  name: string
  spend: number
  roas: number
}

export interface OverviewChannel {
  key: OverviewChannelKey
  name: string
  /** Number of active campaigns on the channel. 0 = no active campaigns. */
  live: number
  total: number
  spend: number
  roas: number
  subs: OverviewSubRow[]
  /** Solid hex used for left border + bar fill + ROAS color. */
  hex: string
  /** 135deg two-stop gradient for the tile background. */
  gradient: string
}

// Static palette overrides for channels where the demo canon and the
// existing overview UI disagree on the exact accent (the overview UI
// uses a pink for retail; the canon uses purple). Preserve the existing
// look-and-feel by keeping the overview-specific palette for retail.
const RETAIL_HEX = '#C2185B'
const RETAIL_GRADIENT = 'linear-gradient(135deg, #FF8FB0, #FFC2D4)'
const AUDIO_HEX_OVERVIEW = '#E0762D'

export const FALLBACK_OVERVIEW: OverviewChannel[] = (
  Object.values(DEMO_CHANNELS) as Array<typeof DEMO_CHANNELS[DemoChannelKey]>
).map((channel) => {
  const subs = subchannelsByChannel(channel.key).map((sub) => ({
    name: sub.name,
    spend: sub.spendCents / 100,
    roas: sub.roas,
  }))
  // Preserve the overview-specific accents on the dormant channels.
  const hex = channel.key === 'retail'
    ? RETAIL_HEX
    : channel.key === 'audio'
      ? AUDIO_HEX_OVERVIEW
      : channel.hex
  const gradient = channel.key === 'retail'
    ? RETAIL_GRADIENT
    : channel.gradient
  return {
    key: channel.key,
    name: channel.label,
    live: channel.liveConnectors,
    total: channel.totalConnectors,
    spend: channel.spendCents / 100,
    roas: channel.roas,
    hex,
    gradient,
    subs,
  }
})

export function overviewTotals(channels: OverviewChannel[]): {
  channelsLive: number
  connectorsLive: number
  monthlySpend: number
  blendedRoas: number
  revenue: number
} {
  const liveChannels = channels.filter((c) => c.live > 0)
  const connectors = liveChannels.reduce((sum, c) => sum + c.live, 0)
  const spend = channels.reduce((sum, c) => sum + c.spend, 0)
  // Spend-weighted blended ROAS across live channels.
  let weighted = 0
  let weight = 0
  for (const c of liveChannels) {
    if (c.spend > 0 && c.roas > 0) {
      weighted += c.spend * c.roas
      weight += c.spend
    }
  }
  const blended = weight > 0 ? weighted / weight : 0
  const revenue = Math.round(spend * (blended || 1))
  return {
    channelsLive: liveChannels.length,
    connectorsLive: connectors,
    monthlySpend: spend,
    blendedRoas: blended,
    revenue,
  }
}

export function isFallbackOverview(channels: OverviewChannel[]): boolean {
  if (channels.length !== FALLBACK_OVERVIEW.length) return false
  return channels.every((channel, index) => {
    const fallback = FALLBACK_OVERVIEW[index]
    return Boolean(fallback)
      && channel.key === fallback.key
      && channel.live === fallback.live
      && channel.total === fallback.total
      && channel.spend === fallback.spend
      && channel.roas === fallback.roas
  })
}
